import React, { useState, useRef } from 'react';
import '../styles/pricingPage.css';
import NavBar from './navBar';
import Sidebar from './sideBar';
import { useCombinedContext } from './context/userContext';
import dayjs from 'dayjs';

const plans = [
  {
    name: 'Free Trial',
    price: 0,
    priceText: 'Free',
    features: [
      'Basic access',
      'Limited support',
      'Community resources',
    ],
    button: 'Start Free',
    highlight: false,
    subtext: '14-day trial',
  },
  {
    name: 'Personal',
    price: 49,
    priceText: '$49',
    features: [
      'All Free features',
      'Full access',
      'Priority support',
    ],
    button: 'Get Started',
    highlight: false,
    subtext: 'Best for individuals',
  },
  {
    name: 'Business',
    price: 99,
    priceText: '$99',
    features: [
      'All Personal features',
      'Team management',
      'Advanced analytics',
    ],
    button: 'Get Started',
    highlight: true,
    badge: 'Recommended',
    subtext: 'For growing teams',
  },
  {
    name: 'Enterprise',
    price: null,
    priceText: 'Custom',
    features: [
      'All Business features',
      'Custom integrations',
      'Dedicated support',
    ],
    button: 'Contact Us',
    highlight: false,
    subtext: 'Custom solutions',
  },
];

const TRIAL_DAYS = 14;

const PricingPage = () => {
  const [billing, setBilling] = useState('monthly');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const sidebarTriggerRef = useRef(null);
  const sidebarRef = useRef(null);
  const { user, setUser, recentChats } = useCombinedContext();

  // Trial state
  const trialStart = user?.trialStart ? dayjs(user.trialStart) : null;
  const trialActive = trialStart && dayjs().diff(trialStart, 'day') < TRIAL_DAYS;
  const trialDaysLeft = trialActive ? TRIAL_DAYS - dayjs().diff(trialStart, 'day') : 0;

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const handleSidebarMouseLeave = (e) => {
    if (sidebarRef.current && e.clientX > sidebarRef.current.getBoundingClientRect().right) {
      setIsSidebarOpen(false);
    }
  };

  const getPrice = (plan) => {
    if (plan.price === 0) return 'Free';
    if (plan.price === null) return 'Contact Us';
    if (billing === 'yearly') {
      if (plan.price) {
        const yearly = plan.price * 12 * 0.83; // 17% off
        return `$${Math.round(yearly)}/yr`;
      }
    }
    return `$${plan.price}/mo`;
  };

  // Start free trial logic
  const handleStartTrial = () => {
    if (!trialActive) {
      const newUser = {
        ...user,
        trialStart: dayjs().toISOString(),
      };
      setUser(newUser);
      localStorage.setItem('user', JSON.stringify(newUser));
    }
  };

  return (
    <div className="pricing-container-wrapper">
      <NavBar toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} trialActive={trialActive} trialDaysLeft={trialDaysLeft} />
      <div
        ref={sidebarTriggerRef}
        className="sidebar-trigger"
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          width: '10px',
          height: '100%',
          zIndex: 1
        }}
      />
      <div
        ref={sidebarRef}
        className={`sidebar-wrapper ${isSidebarOpen ? 'open' : ''}`}
        onMouseLeave={handleSidebarMouseLeave}
      >
        <Sidebar
          onLogout={() => {}}
          onNewChat={() => {}}
          userId={user?.userId}
          recentChats={recentChats}
          activeSessionId={null}
          onChatSelect={() => {}}
          toggleSidebar={toggleSidebar}
          onWriterAiChatSelect={() => {}}
        />
      </div>
      <div className="pricing-main-panel">
        <div className="pricing-page">
          <h2 className="pricing-title">Simple, Transparent Pricing</h2>
          <p className="pricing-subtitle">No hidden fees, no surprises. Choose the plan that's right for you.</p>
          <div className="billing-toggle">
            <button
              className={billing === 'monthly' ? 'active' : ''}
              onClick={() => setBilling('monthly')}
            >
              Monthly
            </button>
            <button
              className={billing === 'yearly' ? 'active' : ''}
              onClick={() => setBilling('yearly')}
            >
              Yearly
              <span className="discount-badge">17% OFF</span>
            </button>
          </div>
          <div className="pricing-cards">
            {plans.map((plan, idx) => {
              // Free Trial card logic
              const isFreeTrial = plan.name === 'Free Trial';
              const cardDisabled = isFreeTrial && trialActive;
              return (
                <div
                  key={plan.name}
                  className={`pricing-card${plan.highlight ? ' recommended' : ''}${cardDisabled ? ' card-disabled' : ''}`}
                  style={cardDisabled ? { pointerEvents: 'none', opacity: 0.6 } : {}}
                >
                  {plan.badge && <div className="plan-badge">{plan.badge}</div>}
                  <h3>{plan.name}</h3>
                  <div className="plan-subtext">{plan.subtext}</div>
                  <div className="plan-price">{getPrice(plan)}</div>
                  {isFreeTrial && trialActive && (
                    <div className="trial-days-left">{trialDaysLeft} days left</div>
                  )}
                  <ul className="plan-features">
                    {plan.features.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                  {isFreeTrial ? (
                    <button
                      className="plan-btn"
                      onClick={handleStartTrial}
                      disabled={trialActive}
                    >
                      {trialActive ? 'Trial Active' : plan.button}
                    </button>
                  ) : (
                    <button className="plan-btn">{plan.button}</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage; 